/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useState } from "react";
import { Avatar, Button, Card, Empty, Modal, Space, Tag, Tooltip } from "antd";
import { EditOutlined, PlusOutlined, CreditCardOutlined, PrinterOutlined, CalendarOutlined, DollarOutlined, FileTextOutlined, UserOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

import AjouterPriseEnCharge from "../../../components/AjouterPriseEnCharge";
import { frenchDate } from "../../../../../variables/functions";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../../variables/numbersFunctions";
import PriseEnCharge from "../../../docs/PriseEnCharge";

const PrisesEnChargeTab = ({ sinistre, setEditMode, editMode, setRefetch }) => {
    const [isPECVisible, setIsPECVisible] = useState(false);
    const [isDocVisible, setIsDocVisible] = useState(false);
    const [priseEnCharge, setPriseEnCharge] = useState(null);
    const [priseEnCharges, setPriseEnCharges] = useState([]);
    const primaryColor = '#242166';

    useEffect(() => {
        axiosInstance
            .get(`/api/prise-en-charge/sinistre/${sinistre.codeId}`)
            .then(({ data }) => {
                setPriseEnCharges(data);
            }).catch((error) => {
                console.error(error);
            });
    }, [sinistre.codeId]);

    const hasAssociatedDecaissement = (pec) => {
        if (!pec) {
            return false;
        }

        const { decaissement } = pec;
        if (!decaissement) {
            return false;
        }

        if (Array.isArray(decaissement)) {
            return decaissement.some((entry) => {
                if (!entry || typeof entry !== "object") {
                    return false;
                }
                const candidateKeys = [
                    entry.codeId,
                    entry.id,
                    entry.code,
                    entry.reference,
                    entry.referenceId,
                    entry.numero,
                ];
                return candidateKeys.some((value) => {
                    if (value === null || value === undefined) {
                        return false;
                    }
                    const normalized = `${value}`.trim();
                    return normalized.length > 0;
                });
            });
        }

        if (typeof decaissement === "object") {
            const candidateKeys = [
                decaissement.codeId,
                decaissement.id,
                decaissement.code,
                decaissement.reference,
                decaissement.referenceId,
                decaissement.numero,
            ];
            return candidateKeys.some((value) => {
                if (value === null || value === undefined) {
                    return false;
                }
                const normalized = `${value}`.trim();
                return normalized.length > 0;
            });
        }

        return Boolean(decaissement);
    };

    const handleOpenNewPEC = () => {
        setEditMode(false);
        setPriseEnCharge(null);
        setIsPECVisible(true);
    };

    const editPriseEnCharge = (item) => {
        setEditMode(true);
        setPriseEnCharge(item);
        setIsPECVisible(true);
    };

    const previewPriseEnCharge = (item) => {
        setPriseEnCharge(item);
        setIsDocVisible(true);
    };

    const initDecaissement = (pec) => {
        Modal.confirm({
            title: `Voulez-vous vraiment transformer la prise en charge ${pec.numeroPriseEnCharge} en un décaissement`,
            content: "Toutes vos actions sont historisées!",
            okText: "Oui, je confirme",
            okType: "danger",
            cancelText: "Annuler",
            onOk: () => {
                axiosInstance
                    .post(`/api/decaissement/init/prise-en-charge/${pec.codeId}/${sinistre.codeId}`)
                    .then(() => {
                        toast("Décaissement initialisé avec succès!");
                        window.location.reload();
                    })
                    .catch(() => {
                        toast("Erreur lors de l'initialisation");
                    });
            },

        });
    };

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: '24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                }}>
                    <div>
                        <h2 style={{
                            margin: 0,
                            color: primaryColor,
                            fontSize: '24px',
                            fontWeight: '600'
                        }}>
                            Prises en Charge
                        </h2>
                        <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '14px' }}>
                            Gérez les prises en charge du sinistre
                        </p>
                    </div>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleOpenNewPEC}
                        disabled={sinistre?.sortDossier === "T" || sinistre?.sortDossier === "S"}
                        size="large"
                        style={{
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            height: '44px',
                            borderRadius: '8px',
                            fontWeight: '500'
                        }}
                    >
                        Nouvelle prise en charge
                    </Button>
                </div>

                {/* Cards Grid */}
                {priseEnCharges?.length ? (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                        gap: '20px'
                    }}>
                        {priseEnCharges.map((item, index) => (
                            <Card
                                key={item.codeId || index}
                                hoverable
                                style={{
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #e8e8e8',
                                    transition: 'all 0.3s ease'
                                }}
                                bodyStyle={{ padding: '20px' }}
                            >
                                {/* Card Header */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'flex-start',
                                    marginBottom: '16px',
                                    paddingBottom: '16px',
                                    borderBottom: '1px solid #f0f0f0'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <Avatar
                                            size={48}
                                            style={{ backgroundColor: primaryColor }}
                                            icon={<FileTextOutlined />}
                                        />
                                        <div>
                                            <h3 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                color: primaryColor
                                            }}>
                                                Prise en charge N° {index + 1}
                                            </h3>
                                            <Tag color={primaryColor} style={{ marginTop: '4px', fontSize: '12px' }}>
                                                {item?.numeroPriseEnCharge || "N/A"}
                                            </Tag>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Content */}
                                <div style={{ marginBottom: '16px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        marginBottom: '12px'
                                    }}>
                                        <UserOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                Victime
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                {item?.victime?.personne?.prenom
                                                    ? `${item?.victime?.personne?.nom} ${item?.victime?.personne?.prenom}`
                                                    : item?.victime?.personne?.nom || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        marginBottom: '12px'
                                    }}>
                                        <FileTextOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                Nature de l'acte
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                {item?.natureActe || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        marginBottom: '12px'
                                    }}>
                                        <DollarOutlined style={{ color: '#52c41a', marginTop: '4px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                Montant
                                            </div>
                                            <div style={{ fontSize: '16px', fontWeight: '600', color: '#52c41a' }}>
                                                {item?.montant ? displayNumbers(item?.montant) : "-"} FCFA
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '8px',
                                        marginBottom: '12px'
                                    }}>
                                        <CalendarOutlined style={{ color: primaryColor, marginTop: '4px' }} />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '12px', color: '#8c8c8c', marginBottom: '2px' }}>
                                                Délai de validité
                                            </div>
                                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#262626' }}>
                                                {item?.delaiValidite ? frenchDate(item?.delaiValidite) : "-"}
                                            </div>
                                        </div>
                                    </div>

                                    <div style={{
                                        fontSize: '12px',
                                        color: '#8c8c8c',
                                        marginTop: '16px',
                                        paddingTop: '12px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        Enregistré le : {frenchDate(item?.createdAt)}
                                    </div>
                                </div>

                                {/* Card Actions */}
                                <Space
                                    style={{
                                        width: '100%',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap'
                                    }}
                                    size="small"
                                >
                                    <Tooltip title="Prévisualiser pour impression">
                                        <Button
                                            icon={<PrinterOutlined />}
                                            onClick={() => previewPriseEnCharge(item)}
                                            style={{ borderRadius: '6px' }}
                                        >
                                            Imprimer
                                        </Button>
                                    </Tooltip>

                                    <Tooltip title="Modifier">
                                        <Button
                                            type="default"
                                            icon={<EditOutlined />}
                                            onClick={() => editPriseEnCharge(item)}
                                            style={{ borderRadius: '6px' }}
                                        >
                                            Modifier
                                        </Button>
                                    </Tooltip>

                                    {!hasAssociatedDecaissement(item) && (
                                        <Tooltip title="Transformer en décaissement">
                                            <Button
                                                danger
                                                icon={<CreditCardOutlined />}
                                                onClick={() => initDecaissement(item)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Initialiser le règlement
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Space>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card style={{ borderRadius: '12px', textAlign: 'center', padding: '40px' }}>
                        <Empty
                            description={
                                <span style={{ color: '#8c8c8c', fontSize: '16px' }}>
                                    Aucune prise en charge enregistrée sur ce dossier sinistre
                                </span>
                            }
                        />
                    </Card>
                )}

                <AjouterPriseEnCharge
                    open={isPECVisible}
                    priseEnCharge={priseEnCharge}
                    setOpen={setIsPECVisible}
                    sinistre={sinistre}
                    edit={editMode}
                    onSuccess={() => {
                        setIsPECVisible(false);
                        setEditMode(false);
                        setPriseEnCharge(null);
                        setRefetch && setRefetch((prev) => !prev);
                    }}
                    onClose={() => {
                        setEditMode(false);
                        setPriseEnCharge(null);
                    }}
                />
                {/* Modal pour preview la prise en charge */}
                <Modal
                    title="Prise en charge"
                    open={isDocVisible}
                    onCancel={() => setIsDocVisible(false)}
                    footer={null}
                    width={1000}
                    zIndex={2000}
                >
                    <PriseEnCharge
                        priseEnCharge={priseEnCharge}
                        sinistre={sinistre}
                    />
                </Modal>
            </div>
        </div>
    );
};

export default PrisesEnChargeTab;
